import { useState, useRef, useEffect } from 'react';

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  initialPosition?: number;
}

function BeforeAfterSlider({ beforeImage, afterImage, initialPosition = 50 }: BeforeAfterSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    
    setSliderPosition(Math.min(Math.max(percentage, 0), 100));
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setSliderPosition(prev => Math.max(prev - 5, 0));
    } else if (e.key === 'ArrowRight') {
      setSliderPosition(prev => Math.min(prev + 5, 100));
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-48 sm:h-56 md:h-64 overflow-hidden rounded-2xl shadow-xl select-none cursor-ew-resize"
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="slider"
      aria-label="Comparador de imagens antes e depois"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(sliderPosition)}
    >
      {/* After Image (background) */}
      <div className="absolute inset-0">
        <img
          src={afterImage}
          alt="Depois do tratamento"
          className="w-full h-full object-cover"
          draggable={false}
        />
        <div className="absolute top-4 right-4 bg-[#E91E63] text-white text-xs font-bold px-3 py-1.5 rounded-full">
          Depois
        </div>
      </div>

      {/* Before Image (overlay) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeImage}
          alt="Antes do tratamento"
          className="w-full h-full object-cover"
          style={{ width: containerRef.current ? `${(containerRef.current.offsetWidth / sliderPosition) * 100}%` : '100%' }}
          draggable={false}
        />
        <div className="absolute top-4 left-4 bg-gray-800 text-white text-xs font-bold px-3 py-1.5 rounded-full">
          Antes
        </div>
      </div>

      {/* Slider Handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <div className="flex gap-1">
            <div className="w-0.5 h-4 bg-gray-400"></div>
            <div className="w-0.5 h-4 bg-gray-400"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfterGallery() {
  // Configuração dos pares de imagens - ALTERE AQUI para trocar as imagens
  const imagePairs = [
    {
      before: '/images/20.jpeg',
      after: '/images/21.jpeg',
    },
    {
      before: '/images/22.jpeg',
      after: '/images/23.jpeg',
    },
    {
      before: 'https://m4digital.co/imagens_clientes/gio-laser/5.jpeg',
      after: 'https://m4digital.co/imagens_clientes/gio-laser/6.jpeg',
    },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-base sm:text-lg md:text-xl font-bold text-black text-center mb-6 px-2 mt-8 sm:mt-0">
        Resultados Reais dos Nossos Clientes
      </h3>
      <div className="grid grid-cols-1 gap-4">
        {imagePairs.map((pair, index) => (
          <BeforeAfterSlider
            key={index}
            beforeImage={pair.before}
            afterImage={pair.after}
            initialPosition={50}
          />
        ))}
      </div>
      <p className="text-xs sm:text-sm text-gray-500 text-center italic mt-4">
        Arraste o controle para comparar os resultados
      </p>
    </div>
  );
}
